// ABOUTME: Site-wide trial countdown ribbon component for subscription status
// ABOUTME: Displays trial days remaining and Subscribe Now button for primary caregivers during trial period (Issue #1026)

import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI } from '../services/api';
import { SubscriptionStatusResponse } from '../types/api';
import { matchesRoutePattern } from '../utils/routeUtils';
import { useCheckoutOverlay } from '../hooks/useCheckoutOverlay';

// Routes where this ribbon should be suppressed (Issue #1234)
const SUPPRESSED_ROUTES = [
  '/setup',
  '/family-setup',
  '/invited-user-onboarding',
];

interface SubscriptionStatusRibbonProps {
  currentPath?: string;
}

/**
 * SubscriptionStatusRibbon Component
 *
 * Displays a site-wide banner during trial period showing:
 * - Days remaining in trial
 * - "Subscribe Now" button (only for primary caregivers)
 *
 * Visibility rules:
 * - Shows ONLY for trial accounts
 * - Hides for free accounts
 * - Hides for active paid subscriptions
 * - Hides on wizard/onboarding routes (Issue #1234)
 * - Not dismissible - always visible during trial
 *
 * @returns {JSX.Element | null} Ribbon element or null if should not display
 */
const SubscriptionStatusRibbon: React.FC<SubscriptionStatusRibbonProps> = ({ currentPath }) => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize checkout overlay hook
  const { openCheckout, isLoading: checkoutLoading } = useCheckoutOverlay({
    familyId: subscriptionStatus?.familyId || 0,
    email: user?.email || '',
    onSuccess: () => {
      // Reload subscription status
      fetchSubscriptionStatus();
    },
    onError: (error) => {
      console.error('Checkout error:', error);
    }
  });

  const fetchSubscriptionStatus = async () => {
    // Don't fetch if ribbon is suppressed on this route
    if (currentPath && matchesRoutePattern(currentPath, SUPPRESSED_ROUTES)) {
      setLoading(false);
      return;
    }

    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // No need to fetch familyId - backend gets it from auth token
      const status = await subscriptionAPI.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      // On error, don't show ribbon
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSubscriptionStatus intentionally omitted; it is stable and adding it would cause extra fetches
  }, [user, currentPath]);

  // Check if ribbon should be suppressed based on current route
  if (currentPath && matchesRoutePattern(currentPath, SUPPRESSED_ROUTES)) {
    return null;
  }

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  // Don't render if no user or no subscription status
  if (!user || !subscriptionStatus) {
    return null;
  }

  // Only show for trial accounts
  if (subscriptionStatus.status !== 'trial') {
    return null;
  }

  // Trial ribbon
  const daysText = subscriptionStatus.daysRemaining === 1 ? 'day' : 'days';

  return (
    <Box
      data-testid="subscription-ribbon"
      role="banner"
      sx={{
        width: '100%',
        backgroundColor: 'info.main',
        color: 'info.contrastText',
        py: 1.5,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body1" fontWeight="medium">
        Trial: {subscriptionStatus.daysRemaining} {daysText} left
      </Typography>

      {subscriptionStatus.isPrimaryCaregiver && (
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={openCheckout}
          disabled={checkoutLoading}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {checkoutLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Subscribe Now'}
        </Button>
      )}
    </Box>
  );
};

export default SubscriptionStatusRibbon;
